import { jwtDecode } from 'jwt-decode'
import localforage from 'localforage'
import { useState, useCallback, type ReactNode, useEffect } from 'react'
import {
  AuthContext,
  AuthDispatchContext,
  type AuthState,
} from '@/components/auth/context'
import { resetMixpanel } from '@/components/mixpanel/client'
import { TOKEN_KEY, REMEMBER_ME_KEY } from '@/config/local-storage'
import { cache } from '@/lib/graphql/cache'

const INITIAL_STATE: AuthState = {
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  twoFA: {
    required: false,
    type: null,
  },
  idUser: null,
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState(INITIAL_STATE)

  const decodeTwoFAToken = useCallback((token: string) => {
    const decodedToken = jwtDecode<{
      twoFA: {
        type: string | null
        required: boolean
      }
      idPendingUser: string | null
    }>(token)

    if (decodedToken?.twoFA?.type) {
      setAuthState((state) => ({
        ...state,
        twoFA: {
          required: true,
          type: decodedToken.twoFA.type,
        },
        idUser: decodedToken.idPendingUser ?? null,
      }))
    }
  }, [])

  const login = useCallback((token: string) => {
    localStorage.setItem(TOKEN_KEY, token)

    const decodedToken = jwtDecode<{
      type: string
      userId: string
    }>(token)

    setAuthState((state) => ({
      ...state,
      isAuthenticated: true,
      twoFA: {
        required: false,
        type: null,
      },
      idUser: decodedToken.userId || null,
    }))
  }, [])

  const logout = useCallback(() => {
    setAuthState({
      isAuthenticated: false,
      twoFA: {
        required: false,
        type: null,
      },
      idUser: null,
    })
    localStorage.removeItem(TOKEN_KEY)
    cache.reset()
    localforage.clear()
    resetMixpanel()
  }, [])

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        try {
          const decodedToken = jwtDecode<{
            type: string
            userId: string
          }>(token)

          if (decodedToken.userId) {
            setAuthState((state) => ({
              ...state,
              isAuthenticated: true,
              idUser: decodedToken.userId,
            }))
          } else {
            logout()
          }
        } catch {
          logout()
        }
      }
    }
  }, [logout])

  const saveRememberMeToken = useCallback((token: string) => {
    localStorage.setItem(REMEMBER_ME_KEY, token)
  }, [])

  return (
    <AuthContext.Provider value={authState}>
      <AuthDispatchContext.Provider
        value={{ login, logout, decodeTwoFAToken, saveRememberMeToken }}
      >
        {children}
      </AuthDispatchContext.Provider>
    </AuthContext.Provider>
  )
}
